import { inject, Injectable } from "@angular/core";
import { generateGuid } from "../functions/common-functions";
import { DbSchemeEnum } from "../enums/db-scheme.enum";
import { ProcedureNameEnum } from "../enums/procedure-name.enum";
import {
  ICreateFrontendLogResult,
  ICreateLogEntryInput,
  ILogEntry,
  LogLevelValue,
} from "./model/log.model";
import { IExecuteProcedureRequest } from "./model/pos.api.model";
import { SharedApiService } from "./api/sharedApi.service";

@Injectable({
  providedIn: "root",
})
export class LogService {
  private readonly sharedApiService = inject(SharedApiService);
  private readonly storageKey = "pos.log.queue";
  private readonly fingerprintKey = "pos.log.browserFingerprint";
  private readonly queueLimit = 100;
  private readonly applicationName = "client-dert-pos";
  private readonly fallbackQueue: ILogEntry[] = [];
  private fallbackFingerprint: string | null = null;
  private isFlushing = false;

  constructor() {
    if (this.canUseBrowserApis()) {
      window.addEventListener("online", () => {
        void this.flushQueue();
      });
    }
  }

  public async createLogEntry(input: ICreateLogEntryInput): Promise<void> {
    try {
      const entry = this.buildLogEntry(input);
      this.enqueue(entry);
      await this.flushQueue();
    } catch (error) {
      console.error("Failed to create log entry.", error);
    }
  }

  public async logDebug(
    message: string,
    tag?: string | null,
    payload?: unknown,
    eventId?: number,
  ): Promise<void> {
    await this.createLogEntry({ message, tag, payload, eventId, level: LogLevelValue.Debug });
  }

  public async logInfo(
    message: string,
    tag?: string | null,
    payload?: unknown,
    eventId?: number,
  ): Promise<void> {
    await this.createLogEntry({ message, tag, payload, eventId, level: LogLevelValue.Info });
  }

  public async logWarning(
    message: string,
    tag?: string | null,
    payload?: unknown,
    eventId?: number,
  ): Promise<void> {
    await this.createLogEntry({ message, tag, payload, eventId, level: LogLevelValue.Warning });
  }

  public async logError(
    message: string,
    tag?: string | null,
    payload?: unknown,
    eventId?: number,
  ): Promise<void> {
    await this.createLogEntry({ message, tag, payload, eventId, level: LogLevelValue.Error });
  }

  public async logCritical(
    message: string,
    tag?: string | null,
    payload?: unknown,
    eventId?: number,
  ): Promise<void> {
    await this.createLogEntry({ message, tag, payload, eventId, level: LogLevelValue.Critical });
  }

  public getBrowserFingerprint(): string {
    if (!this.canUseBrowserApis()) {
      this.fallbackFingerprint ??= generateGuid();
      return this.fallbackFingerprint;
    }

    try {
      const storedFingerprint = localStorage.getItem(this.fingerprintKey);

      if (storedFingerprint) {
        return storedFingerprint;
      }

      const fingerprint = generateGuid();
      localStorage.setItem(this.fingerprintKey, fingerprint);
      return fingerprint;
    } catch {
      this.fallbackFingerprint ??= generateGuid();
      return this.fallbackFingerprint;
    }
  }

  public getQueuedCount(): number {
    return this.readQueue().length;
  }

  public clearQueue(): void {
    this.writeQueue([]);
  }

  public async flushQueue(): Promise<void> {
    if (this.isFlushing || !this.canUseBrowserApis() || !this.isOnline()) {
      return;
    }

    this.isFlushing = true;

    try {
      while (true) {
        const queue = this.readQueue();
        const nextEntry = queue[0];

        if (!nextEntry) {
          return;
        }

        try {
          await this.sendLogEntry(nextEntry);
          queue.shift();
          this.writeQueue(queue);
        } catch (error) {
          console.error("Failed to send log entry.", error);
          return;
        }
      }
    } finally {
      this.isFlushing = false;
    }
  }

  private enqueue(entry: ILogEntry): void {
    const queue = this.readQueue();
    queue.push(entry);

    while (queue.length > this.queueLimit) {
      queue.shift();
    }

    this.writeQueue(queue);
  }

  private async sendLogEntry(entry: ILogEntry): Promise<void> {
    const request: IExecuteProcedureRequest<ILogEntry> = {
      DbScheme: DbSchemeEnum.Dert,
      ProcedureName: ProcedureNameEnum.CreateFrontendLog,
      Parameters: entry,
    };

    await this.sharedApiService.executeProcedure<ILogEntry, ICreateFrontendLogResult>(request);
  }

  private buildLogEntry(input: ICreateLogEntryInput): ILogEntry {
    const browserFingerprint = input.browserFingerprint ?? this.getBrowserFingerprint();
    const time = this.normalizeDateTime(input.time ?? new Date());

    return {
      ApplicationName: input.applicationName ?? this.applicationName,
      EventId: input.eventId ?? 0,
      Level: input.level ?? LogLevelValue.Info,
      Message: input.message,
      Tag: input.tag ?? null,
      Time: time,
      EventTask: this.normalizeNumber(input.eventTask),
      Keywords: this.normalizeNumber(input.keywords),
      Opcode: this.normalizeNumber(input.opcode),
      Payload: this.serializePayload(input.payload, browserFingerprint),
      ProcessId: this.normalizeNumber(input.processId),
      ThreadId: this.normalizeNumber(input.threadId),
      Created: time,
      BrowserFingerprint: browserFingerprint,
    };
  }

  private normalizeDateTime(value: string | Date): string {
    if (value instanceof Date) {
      return value.toISOString();
    }

    return value;
  }

  private normalizeNumber(value?: number): number {
    return Number.isFinite(value) ? Math.trunc(value) : 0;
  }

  private serializePayload(payload: unknown, browserFingerprint: string): string {
    const normalizedPayload = this.normalizePayloadValue(payload);

    if (this.isPlainObject(normalizedPayload)) {
      const { browserFingerprint: _browserFingerprint, BrowserFingerprint: _BrowserFingerprint, ...rootPayload } = normalizedPayload as Record<string, unknown>;

      return JSON.stringify({
        BrowserFingerprint: browserFingerprint,
        ...rootPayload,
      });
    }

    return JSON.stringify({
      BrowserFingerprint: browserFingerprint,
      payload: normalizedPayload,
    });
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date) && !(value instanceof Error);
  }

  private normalizePayloadValue(payload: unknown): unknown {
    if (payload === undefined || payload === null) {
      return null;
    }

    if (payload instanceof Error) {
      return {
        name: payload.name,
        message: payload.message,
        stack: payload.stack,
      };
    }

    if (typeof payload === "string") {
      return payload;
    }

    if (typeof payload === "number" || typeof payload === "boolean") {
      return payload;
    }

    if (payload instanceof Date) {
      return payload.toISOString();
    }

    try {
      return JSON.parse(JSON.stringify(payload));
    } catch {
      return String(payload);
    }
  }

  private readQueue(): ILogEntry[] {
    if (!this.canUseBrowserApis()) {
      return [...this.fallbackQueue];
    }

    try {
      const serializedQueue = localStorage.getItem(this.storageKey);

      if (!serializedQueue) {
        return [];
      }

      const parsedQueue = JSON.parse(serializedQueue);
      return Array.isArray(parsedQueue) ? parsedQueue : [];
    } catch {
      return [...this.fallbackQueue];
    }
  }

  private writeQueue(queue: ILogEntry[]): void {
    const limitedQueue = queue.slice(-this.queueLimit);

    if (!this.canUseBrowserApis()) {
      this.fallbackQueue.splice(0, this.fallbackQueue.length, ...limitedQueue);
      return;
    }

    try {
      localStorage.setItem(this.storageKey, JSON.stringify(limitedQueue));
    } catch {
      this.fallbackQueue.splice(0, this.fallbackQueue.length, ...limitedQueue);
    }
  }

  private canUseBrowserApis(): boolean {
    return typeof window !== "undefined" && typeof localStorage !== "undefined";
  }

  private isOnline(): boolean {
    return typeof navigator !== "undefined" ? navigator.onLine : true;
  }
}